import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { OrganizationDto, SessionDto, UserDto } from '@asrt/types';
import { ApiError, api, errorMessage, setCsrfToken } from './api';

export interface MeResponse {
  user: UserDto;
  organizations: OrganizationDto[];
  sessions: SessionDto[];
}

export interface LoginResponse {
  user?: UserDto;
  session?: unknown;
  csrfToken?: string;
  twoFactorRequired?: boolean;
  challengeId?: string;
  expiresAt?: string;
  channels?: string[];
  note?: string;
  [key: string]: unknown;
}

interface AuthContextValue {
  user: UserDto | null;
  organizations: OrganizationDto[];
  sessions: SessionDto[];
  loading: boolean;
  /** True while /v1/auth/me failed with something other than 401 (surfaced, never faked). */
  error: ApiError | null;
  login: (email: string, password: string, totp?: string) => Promise<LoginResponse>;
  requestEmailCode: (email: string) => Promise<unknown>;
  verifyTwoFactor: (challengeId: string, code: string) => Promise<{ user?: UserDto; csrfToken?: string }>;
  signup: (input: { email: string; password: string; username: string; displayName?: string }) => Promise<unknown>;
  logout: () => Promise<void>;
  refresh: () => Promise<unknown>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const me = useQuery<MeResponse | null>({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.get<MeResponse>('/v1/auth/me');
      } catch (e) {
        if (e instanceof ApiError && (e.isUnauthorized || e.code === 'NETWORK_ERROR')) return null;
        throw e;
      }
    },
    retry: false,
    staleTime: 30_000,
  });

  const login = React.useCallback(async (email: string, password: string, totp?: string) => {
    const body: Record<string, string> = { email, password };
    if (totp) body.totp = totp;
    const res = await api.post<LoginResponse>('/v1/auth/login', body);
    setCsrfToken(res.csrfToken);
    return res;
  }, []);

  const requestEmailCode = React.useCallback((email: string) => api.post('/v1/auth/2fa/challenge', { email }), []);

  const verifyTwoFactor = React.useCallback(async (challengeId: string, code: string) => {
    const res = await api.post<{ user?: UserDto; csrfToken?: string }>('/v1/auth/2fa/verify', { challengeId, code });
    setCsrfToken(res.csrfToken);
    return res;
  }, []);

  const signup = React.useCallback(
    async (input: { email: string; password: string; username: string; displayName?: string }) => {
      const res = await api.post<{ csrfToken?: string }>('/v1/auth/signup', input);
      setCsrfToken(res?.csrfToken);
      return res;
    },
    [],
  );

  const logout = React.useCallback(async () => {
    try {
      await api.post('/v1/auth/logout');
    } catch (e) {
      // Logging out on an expired session is still "logged out" for the UI.
      if (!(e instanceof ApiError && e.isUnauthorized)) throw e;
    }
    setCsrfToken(null);
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      user: me.data?.user ?? null,
      organizations: me.data?.organizations ?? [],
      sessions: me.data?.sessions ?? [],
      loading: me.isLoading || me.isFetching,
      error: me.error ? (me.error instanceof ApiError ? me.error : new ApiError(0, 'INTERNAL_ERROR', errorMessage(me.error))) : null,
      login,
      requestEmailCode,
      verifyTwoFactor,
      signup,
      logout,
      refresh: () => me.refetch(),
    }),
    [me.data, me.isLoading, me.isFetching, me.error, me.refetch, login, requestEmailCode, verifyTwoFactor, signup, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
